import { useMemo, useCallback, useState, useEffect } from "react"
import { TableHeaderItem, TableSortBy, TablePagination } from "../../../hooks/useTable"
import { Property } from "../../../ducks/property/types"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch } from "../../../store"
import { propertySelectors } from "../../../ducks/property/selectors"
import { propertyActions } from "../../../ducks/property/actions"
import { ClickableText } from "../../atoms/ClickableText";
import { useDebounce } from "../../../utils/debounce";
import { propertyTypeListApi, PropertyListApi } from '../../../core/http/httpClient';
import { appActions } from "../../../ducks/app"
import { ToastType } from "../../atoms/Toast"
import { PropertyCreateRequestBody } from "../../../core/api/requests/property/create/types"
import { PropertyEditRequestBody } from "../../../core/api/requests/property/edit/types"
import { useModalWithProtection } from "../../../hooks/useModalWithProtection"
import { getErrorMessage } from "../../../utils/apiError"
import { HotPropertyToggle } from "../../atoms/HotPropertyToggle"

export const useProperty = () => {
    const dispatch = useDispatch<AppDispatch>();

    const properties = useSelector(propertySelectors.properties);
    const totalItems = useSelector(propertySelectors.totalItems);
    const pageSize = useSelector(propertySelectors.pageSize);
    const totalPages = useSelector(propertySelectors.totalPages);

    const selectedPropertyDetail = useSelector(propertySelectors.selectedPropertyDetail);

    // Local pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Modal state for property detail
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    // handle modal
    const { isOpen: isCreateModalOpen, openModal: handleOpenCreateModal, closeModal: handleCloseCreateModal } = useModalWithProtection();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedPropertyEdit, setSelectedPropertyEdit] = useState<Property | null>(null);
    const handleOpenEditModal = () => setIsEditModalOpen(true);
    const handleCloseEditModal = () => setIsEditModalOpen(false);

    // Filter states
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedHot, setSelectedHot] = useState('');
    const [propertyTypes, setPropertyTypes] = useState<{ id: number, name: string }[]>([]);

    // Loading and error states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Debounced search value
    const debouncedSearchText = useDebounce(searchText, 400);

    // Fetch property types from API
    useEffect(() => {
        propertyTypeListApi.call({} as any).then(res => {
            setPropertyTypes(
                res.data.map((item: any) => ({ id: Number(item.id), name: item.name }))
            );
        });
    }, []);

    const handleOpenDetailModal = useCallback((property: Property) => {
        setIsDetailModalOpen(true);
        setDetailLoading(true);
        dispatch(propertyActions.getPropertyDetails.started({ id: property.id } as any));
    }, [dispatch]);

    const refreshPropertyDetails = useCallback((propertyId: string) => {
        dispatch(propertyActions.getPropertyDetails.started({ id: propertyId } as any));
    }, [dispatch]);

    useEffect(() => {
        if (isDetailModalOpen && selectedPropertyDetail) {
            setDetailLoading(false);
        }
    }, [isDetailModalOpen, selectedPropertyDetail]);

    const handleCloseDetailModal = useCallback(() => {
        setIsDetailModalOpen(false);
        setDetailLoading(false);
    }, []);

    // Flexible fetch for property list
    const fetchDataFunction = useCallback((sort: TableSortBy, pagination?: TablePagination, filters?: any) => {
        const fetchPage = pagination?.page ?? currentPage;
        const fetchPageSize = pagination?.size ?? pageSize;
        const params: any = {
            page: fetchPage,
            page_size: fetchPageSize,
        };
        if (filters?.searchText) params.search = filters.searchText;
        if (filters?.selectedStatus) params.status = filters.selectedStatus;
        if (filters?.selectedType) params.type = filters.selectedType;
        if (filters?.selectedHot) params.is_hot = filters.selectedHot;
        setLoading(true);
        setError(null);
        PropertyListApi.call(params as any)
            .then(res => {
                // Update Redux state with the result
                dispatch(propertyActions.fetchProperties.done({
                    params,
                    result: res.data
                }));
                setLoading(false);
            })
            .catch(err => {
                setError('Failed to fetch properties');
                setLoading(false);
            });
    }, [dispatch, currentPage, pageSize]);

    // Effect: refetch when filters or pagination change
    useEffect(() => {
        const params: any = {
            page: currentPage,
            page_size: pageSize,
        };
        if (debouncedSearchText) params.search = debouncedSearchText;
        if (selectedStatus) params.status = selectedStatus;
        if (selectedType) params.type = selectedType;
        if (selectedHot) params.is_hot = selectedHot;

        setLoading(true);
        setError(null);
        PropertyListApi.call(params as any)
            .then(res => {
                // Update Redux state with the result
                dispatch(propertyActions.fetchProperties.done({
                    params,
                    result: res.data
                }));
                setLoading(false);
            })
            .catch(err => {
                setError('Failed to fetch properties');
                setLoading(false);
            });
    }, [debouncedSearchText, selectedStatus, selectedType, selectedHot, currentPage, pageSize, dispatch]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchText, selectedStatus, selectedType, selectedHot]);

    const handleRowDelete = useCallback(
        (data: Property) => {
            dispatch(
                appActions.showModal({
                    title: "Delete Confirmation",
                    subTitle: "Are you sure you want to delete?",
                    buttons: [
                        {
                            text: "Cancel",
                            variant: "border",
                            onClick: () => dispatch(appActions.hideModal()),
                        },
                        {
                            text: "Delete",
                            variant: "filled",
                            onClick: () => {
                                dispatch(appActions.hideModal());
                                dispatch(appActions.showLoading());
                                dispatch(
                                    propertyActions.deleteProperty.started({
                                        id: data.id,
                                        onComplete: () => {
                                            dispatch(appActions.hideLoading());
                                            // Refresh with current pagination and filters
                                            fetchDataFunction(
                                                { field: "name", order: "asc" },
                                                { page: currentPage, size: pageSize },
                                                { searchText: debouncedSearchText, selectedStatus, selectedType, selectedHot }
                                            );
                                        }
                                    })
                                )
                            }
                        }
                    ]
                })
            )
        },
        [dispatch, fetchDataFunction, currentPage, pageSize, debouncedSearchText, selectedStatus, selectedType, selectedHot]
    )

    const { toast, showToast } = usePropertyToast();

    const handleToggleHot = useCallback((property: Property) => {
        const newIsHot = !property.is_hot;
        dispatch(appActions.showLoading());
        dispatch(
            propertyActions.updateProperty.started({
                id: property.id,
                is_hot: String(newIsHot),
                onComplete: () => {
                    dispatch(appActions.hideLoading());
                },
                onSuccess: () => {
                    showToast(`Property ${newIsHot ? 'marked as Hot' : 'removed from Hot Properties'}`, 'success');
                    fetchDataFunction(
                        { field: "name", order: "asc" },
                        { page: currentPage, size: pageSize },
                        { searchText: debouncedSearchText, selectedStatus, selectedType, selectedHot }
                    );
                },
                onError: (err: any) => {
                    const msg = getErrorMessage(err, "Failed to update hot status");
                    showToast(msg, 'error');
                    // Refresh data on error to revert any optimistic UI changes
                    fetchDataFunction(
                        { field: "name", order: "asc" },
                        { page: currentPage, size: pageSize },
                        { searchText: debouncedSearchText, selectedStatus, selectedType, selectedHot }
                    );
                }
            } as any)
        );
    }, [dispatch, fetchDataFunction, currentPage, pageSize, debouncedSearchText, selectedStatus, selectedType, selectedHot, showToast]);

    const tableHeaders: TableHeaderItem<Property>[] = useMemo(() => {
        return [
            {
                id: 1,
                title: "Property Code",
                dataKey: "property_code",
                dataMapFunction: (property) => ({
                    text: property.property_code,
                    onClick: () => handleOpenDetailModal(property),
                }),
                customComponent: ClickableText,
            },
            {
                id: 2,
                title: "Property Name",
                dataKey: "property_name",
            },
            {
                id: 3,
                title: "Property Type",
                dataKey: "property_type",
                dataMapFunction: (property) => property.property_type?.name || "",
            },
            {
                id: 4,
                title: "Property Status",
                dataKey: "property_status",
            },
            {
                id: 5,
                title: "District",
                dataKey: "district",
                dataMapFunction: (property) => property.district?.name || "",
            },
            {
                id: 6,
                title: "Commune",
                dataKey: "commune",
                dataMapFunction: (property) => property.commune?.name || "",
            },
            {
                id: 7,
                title: "Hot",
                dataKey: "is_hot",
                dataMapFunction: (property) => ({
                    property: property,
                    onToggle: handleToggleHot,
                }),
                customComponent: HotPropertyToggle,
            },
            {
                id: 8,
                title: "Actions",
                dataKey: "id",
                dataMapFunction: (property) => "",
            }
        ]
    }, [handleOpenDetailModal, handleToggleHot])

    // Add a handler to change page
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    // Only show pagination if there's more than one page
    const hasPagination = totalPages > 1;

    return {
        tableHeaders,
        properties,
        totalItems,
        currentPage,
        pageSize,
        fetchDataFunction,
        isDetailModalOpen,
        selectedProperty: selectedPropertyDetail,
        detailLoading,
        handleOpenDetailModal,
        handleCloseDetailModal,
        refreshPropertyDetails,
        hasPagination,
        handlePageChange,
        toast,
        showToast,
        // Filters
        searchText,
        setSearchText,
        selectedStatus,
        setSelectedStatus,
        selectedType,
        setSelectedType,
        selectedHot,
        setSelectedHot,
        propertyTypes,
        loading,
        error,
        handleRowDelete,
        handleToggleHot,
        isCreateModalOpen,
        isEditModalOpen,
        selectedPropertyEdit,
        setSelectedPropertyEdit,
        handleCloseCreateModal,
        handleOpenCreateModal,
        handleCloseEditModal,
        handleOpenEditModal,
        refetchProperties: () =>
            fetchDataFunction(
                { field: "name", order: "asc" },
                { page: currentPage, size: pageSize },
                { searchText: debouncedSearchText, selectedStatus, selectedType, selectedHot }
            )
    }
}

export const usePropertyToast = () => {
    const [toast, setToast] = useState<{
        message: string;
        type: ToastType;
        visible: boolean;
    }>({ message: "", type: "success", visible: false });

    const showToast = (message: string, type: ToastType = "success") => {
        setToast({ message, type, visible: true });
        setTimeout(() => {
            setToast((t) => ({ ...t, visible: false }));
        }, 2000);
    };

    const hideToast = () => setToast((t) => ({ ...t, visible: false }));

    return { toast, showToast, hideToast };
};

export const usePropertyRegistration = (
    showToast: (msg: string, type?: ToastType) => void,
    refetchProperties?: () => void,
    closeModal?: () => void
) => {
    const dispatch = useDispatch<AppDispatch>();

    const handleRegisterProperty = useCallback(
        (
            data: PropertyCreateRequestBody,
            onError: (message: string) => void,
            onSuccess?: (result: any) => void
        ) => {
            dispatch(
                propertyActions.createNewProperty.started({
                    ...data,
                    onComplete: () => { },
                    onSuccess: (result: any) => {
                        showToast("Property created successfully!", "success");
                        setTimeout(() => {
                            if (refetchProperties) refetchProperties();
                            if (closeModal) closeModal();
                            if (onSuccess) onSuccess(result);
                        }, 1200);
                    },
                    onError: (err: any) => {
                        const msg = getErrorMessage(err, "Failed to create property. Please try again.");
                        showToast(msg, "error");
                        if (onError) onError(msg);
                    }
                })
            )
        },
        [dispatch, showToast, refetchProperties, closeModal]
    )
    return { handleRegisterProperty };
}

export const usePropertyEdit = (
    showToast: (msg: string, type?: ToastType) => void,
    refetchProperties?: () => void,
    closeModal?: () => void
) => {
    const dispatch = useDispatch<AppDispatch>();

    const handleEditProperty = useCallback(
        (
            data: PropertyEditRequestBody,
            id: string,
            onError: (message: string) => void,
            onSuccess?: (result: any) => void
        ) => {
            return dispatch(
                propertyActions.updateProperty.started({
                    ...data,
                    id,
                    onComplete: () => { },
                    onSuccess: (result: any) => {
                        showToast("Updated successfully!", "success");
                        setTimeout(() => {
                            if (refetchProperties) refetchProperties();
                            if (closeModal) closeModal();
                            if (onSuccess) onSuccess(result);
                        }, 1200);
                    },
                    onError: (err: any) => {
                        const msg = getErrorMessage(err, "Failed to update property. Please try again.");
                        showToast(msg, "error");
                        if (onError) onError(msg);
                    }
                })
            )
        }, [dispatch, showToast, refetchProperties, closeModal]
    )
    return { handleEditProperty };
}
